import { useState } from "react";
import { useProductUnits, ProductUnitFormData, ProductUnit } from "@/features/produtos/hooks/useProductUnits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Ban } from "lucide-react";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

export default function UnidadesProduto() {
  const { units, isLoading, createUnit, updateUnit, deleteUnit } = useProductUnits();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<ProductUnit | null>(null);
  const [inativarId, setInativarId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProductUnitFormData>({
    sigla: "",
    descricao: "",
    isPrincipal: false,
    isPadrao: false,
    vendaFracionada: false,
    isAtivo: true,
  });

  const [busca, setBusca] = useState("");

  const unitsFiltradas = busca
    ? units.filter(u =>
        u.sigla?.toLowerCase().includes(busca.toLowerCase()) ||
        u.descricao?.toLowerCase().includes(busca.toLowerCase()))
    : units;

  const paginacao = usePaginacaoLocal(unitsFiltradas);

  const handleOpenDialog = (unit?: ProductUnit) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        sigla: unit.sigla || "",
        descricao: unit.descricao || "",
        isPrincipal: unit.isPrincipal,
        isPadrao: unit.isPadrao,
        vendaFracionada: unit.vendaFracionada,
        isAtivo: unit.isAtivo,
        fatorConversao: unit.fatorConversao,
        volume: unit.volume,
        peso: unit.peso,
        unidadeVolume: unit.unidadeVolume || "",
        unidadePeso: unit.unidadePeso || "",
      });
    } else {
      setEditingUnit(null);
      setFormData({ sigla: "", descricao: "", isPrincipal: false, isPadrao: false, vendaFracionada: false, isAtivo: true });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUnit) {
      await updateUnit.mutateAsync({ id: editingUnit.id, ...formData });
    } else {
      await createUnit.mutateAsync(formData);
    }
    setDialogOpen(false);
  };

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Unidades de Produto</h1>
          <p className="text-muted-foreground">Gerencie as unidades de medida dos produtos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Unidade
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Buscar por sigla ou descrição..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sigla</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead className="text-center">Principal</TableHead>
              <TableHead className="text-center">Padrão</TableHead>
              <TableHead className="text-center">Venda Frac.</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map((unit) => (
              <TableRow key={unit.id}>
                <TableCell className="font-mono font-medium">{unit.sigla || "-"}</TableCell>
                <TableCell>{unit.descricao || "-"}</TableCell>
                <TableCell className="text-center">{unit.isPrincipal ? "✓" : "-"}</TableCell>
                <TableCell className="text-center">{unit.isPadrao ? "✓" : "-"}</TableCell>
                <TableCell className="text-center">{unit.vendaFracionada ? "✓" : "-"}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={unit.isAtivo ? "success" : "secondary"}>
                    {unit.isAtivo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(unit)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {unit.isAtivo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(unit.id)}>
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhuma unidade encontrada para esta busca." : "Nenhuma unidade cadastrada"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editingUnit ? "Editar Unidade" : "Nova Unidade"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sigla">Sigla *</Label>
                <Input
                  id="sigla"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Input
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fatorConversao">Fator de Conversão</Label>
                <Input
                  id="fatorConversao"
                  type="number"
                  step="0.0001"
                  value={formData.fatorConversao ?? ""}
                  onChange={(e) => setFormData({ ...formData, fatorConversao: e.target.value ? Number(e.target.value) : undefined })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="unidadeVolume">Unidade Volume</Label>
                <Input
                  id="unidadeVolume"
                  value={formData.unidadeVolume || ""}
                  onChange={(e) => setFormData({ ...formData, unidadeVolume: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="isPrincipal"
                  checked={formData.isPrincipal}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPrincipal: checked })}
                />
                <Label htmlFor="isPrincipal">Principal</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="isPadrao"
                  checked={formData.isPadrao}
                  onCheckedChange={(checked) => setFormData({ ...formData, isPadrao: checked })}
                />
                <Label htmlFor="isPadrao">Padrão</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="vendaFracionada"
                  checked={formData.vendaFracionada}
                  onCheckedChange={(checked) => setFormData({ ...formData, vendaFracionada: checked })}
                />
                <Label htmlFor="vendaFracionada">Venda Fracionada</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="isAtivo"
                  checked={formData.isAtivo}
                  onCheckedChange={(checked) => setFormData({ ...formData, isAtivo: checked })}
                />
                <Label htmlFor="isAtivo">Ativo</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar unidade</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar esta unidade de produto?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { deleteUnit.mutate(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
